import { Component, Inject, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ToolbarDrawerService } from '../../../shared/toolbar/toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../../../shared/contracts/toolbar-control.tokens';
import { VesselFinderMapController, VesselFinderVesselRow } from '../../vesselfinder-map.controller';
import { BaseControl } from '../../../shared/toolbar/controls/base-control';

/** "12 min ago" / "2 h ago" / "6 d ago" — duplicated from Voyage Radar's own
 *  `relativeFixAge` (providers do not import each other's files). */
function relativeFixAge(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
}

/**
 * VesselFinder's "Watched Vessels" control — same UI/behaviour as Voyage
 * Radar's `VesselHistoryControlComponent`: a STANDING button (always
 * visible, even with nothing watched yet — a search-only entry point would
 * be one more thing to discover), a drawer listing every hull with
 * center/remove per row, and a "Clear all" header action.
 *
 * `list` (`vfCtrl.state$.locatedVessels`) is seeded from the sensor's
 * PERSISTED `vesselHistory` on task load (`_attachVesselFinder`, host
 * component) — so it lists every watched vessel by default, not only what
 * this session searched/located. Not every row is necessarily drawn on the
 * map, though (some carry no stored fix), which is what the `ON MAP` badge
 * (`isOnMap`, mirrors the sidebar panel's own `onMapKeys`) is for.
 */
@Component({
  selector: 'app-vf-watched-vessels-control',
  template: `
    <ng-container *ngIf="(vfCtrl.state$ | async)?.locatedVessels as list">
      <button *ngIf="list.length" #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Watched Vessels"
              [matTooltip]="'Watched Vessels · ' + list.length + ' on the map'"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">bookmarks</mat-icon>
      </button>
    </ng-container>

    <ng-template #panel>
      <app-toolbar-drawer *ngIf="(vfCtrl.state$ | async)?.locatedVessels as list"
                          title="Watched Vessels"
                          [subtitle]="list.length + ' on the map'"
                          actionLabel="Clear all"
                          [actionDisabled]="!list.length"
                          (action)="clearAll()"
                          (close)="drawer.close()">
        <div class="vh-list">
          <div class="vh-row" *ngFor="let r of list; trackBy: trackRow">
            <span class="vh-row__diamond" [class.is-stale]="r.stale"></span>
            <span class="vh-row__text">
              <span class="vh-row__name">{{ r.name || identifierLabel(r) }}</span>
              <span class="vh-row__age" *ngIf="age(r) as a">
                {{ r.stale ? 'last known position · ' : 'last position · ' }}{{ a }}
              </span>
            </span>
            <button type="button" class="vh-row__btn"
                    [disabled]="!hasFix(r)"
                    (click)="center(r)"
                    aria-label="Center on vessel"
                    [matTooltip]="hasFix(r) ? 'Center the map on this vessel' : 'No stored position for this vessel'"
                    matTooltipPosition="above">
              <mat-icon class="material-icons-outlined">my_location</mat-icon>
            </button>
            <button type="button" class="vh-row__btn"
                    (click)="takeOffMap(r)"
                    aria-label="Take vessel off the map"
                    matTooltip="Take off the map — stays in Watched Vessels" matTooltipPosition="above">
              <mat-icon class="material-icons-outlined">layers_clear</mat-icon>
            </button>
          </div>
        </div>
      </app-toolbar-drawer>
    </ng-template>`,
  styles: [`
    .vh-list {
      display: flex;
      flex-direction: column;
      max-height: 260px;
      overflow-y: auto;
    }
    .vh-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 2px;
    }
    .vh-row + .vh-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .vh-row__diamond {
      flex: 0 0 auto;
      width: 9px;
      height: 9px;
      background: #B45309;
      transform: rotate(45deg);
    }
    .vh-row__diamond.is-stale {
      background: transparent;
      border: 1.5px solid #9aa0a6;
    }
    .vh-row__text {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.25;
    }
    .vh-row__name {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .vh-row__age { font-size: 10.5px; opacity: 0.65; }
    .vh-row__btn {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: inherit;
      cursor: pointer;
      padding: 0;
    }
    .vh-row__btn:hover { background: rgba(128, 128, 128, 0.15); }
    .vh-row__btn:disabled { opacity: 0.35; cursor: default; }
    .vh-row__btn:disabled:hover { background: transparent; }
    .vh-row__btn .mat-icon { font-size: 16px; width: 16px; height: 16px; line-height: 16px; }
  `],
})
export class VfWatchedVesselsControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public vfCtrl: VesselFinderMapController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  identifierLabel(r: VesselFinderVesselRow): string {
    return r.mmsi ? `MMSI ${r.mmsi}` : r.imo ? `IMO ${r.imo}` : '—';
  }

  /** Does this row have a stored fix to center on? Rows seeded from history without one show a disabled button, not a silent no-op. */
  hasFix(r: VesselFinderVesselRow): boolean {
    return r.latitude != null && r.longitude != null;
  }

  isOnMap(r: VesselFinderVesselRow): boolean {
    const key = r.mmsi ?? r.imo;
    return !!key && this.vfCtrl.isOnMap(key);
  }

  center(r: VesselFinderVesselRow): void {
    const key = r.mmsi ?? r.imo;
    if (key) this.vfCtrl.centerVessel(key);
  }

  takeOffMap(r: VesselFinderVesselRow): void {
    const key = r.mmsi ?? r.imo;
    if (key) this.vfCtrl.takeOffMap(key);
    if ((this.vfCtrl.snapshot().locatedVessels?.length ?? 0) === 0) {
      this.drawer.close();
    }
  }

  clearAll(): void {
    this.vfCtrl.clearAll();
    this.drawer.close();
  }

  age(r: VesselFinderVesselRow): string | null {
    return relativeFixAge(r.timeUtc);
  }

  trackRow(_i: number, r: VesselFinderVesselRow): string {
    return r.mmsi ?? r.imo ?? '';
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
  }
}
